import {
  LOCALE_ID,
  PLATFORM_ID,
  REQUEST,
  Provider,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  DEFAULT_COUNTRY_CODE,
  getCountryConfig,
  parseLanguageCookie,
  resolveBcp47FromLanguageCode,
  resolveCountryFromDomain,
  resolveLanguageFromDomain,
  seoSegments,
  type AppStore,
  type CountryCode,
  type LanguageCode,
  type SeoSegments,
} from '@handyhand/i18n';
import type { AppEnv } from '../../environments/app-env';
import { pickEnv } from '../../environments/env-registry';
import {
  AppStoreToken,
  CountryToken,
  EnvToken,
  LanguageToken,
  SeoSegmentsToken,
} from './env.tokens';

/**
 * Read the current request hostname on both platforms:
 *   - Browser: `window.location.hostname`.
 *   - SSR: Angular 21's `REQUEST` (Fetch `Request`) wired by
 *     `AngularNodeAppEngine.handle(req)`.
 * Returns an empty string when no host can be determined (tests, build-time
 * prerender, etc.); callers must fall back to `DEFAULT_COUNTRY_CODE`.
 */
export function resolveHostname(): string {
  const platformId = inject(PLATFORM_ID);
  const request = inject(REQUEST, { optional: true });

  if (isPlatformBrowser(platformId)) {
    return globalThis.window?.location?.hostname ?? '';
  }

  if (request?.url) {
    try {
      return new URL(request.url).hostname;
    } catch {
      return '';
    }
  }
  return '';
}

export function resolveCountry(): CountryCode {
  const hostname = resolveHostname();
  return resolveCountryFromDomain(hostname) ?? DEFAULT_COUNTRY_CODE;
}

/**
 * Read the language cookie on both platforms:
 *   - Browser: `document.cookie`.
 *   - SSR: the `Cookie` header on Angular 21's `REQUEST` (Fetch `Request`).
 * Returns the validated `LanguageCode` or `null` when absent / unsupported.
 */
export function resolveLanguageFromCookie(): LanguageCode | null {
  const platformId = inject(PLATFORM_ID);
  const request = inject(REQUEST, { optional: true });

  if (isPlatformBrowser(platformId)) {
    return parseLanguageCookie(globalThis.document?.cookie ?? '');
  }
  return parseLanguageCookie(request?.headers?.get('cookie'));
}

export function resolveLanguage(): LanguageCode {
  const fromCookie = resolveLanguageFromCookie();
  if (fromCookie) {
    return fromCookie;
  }
  const hostname = resolveHostname();
  const fromDomain = resolveLanguageFromDomain(hostname);
  if (fromDomain) {
    return fromDomain;
  }
  const country = resolveCountryFromDomain(hostname) ?? DEFAULT_COUNTRY_CODE;
  return getCountryConfig(country).defaultLanguage;
}

/**
 * Providers wiring `CountryToken`, `EnvToken`, and Angular's `LOCALE_ID` to
 * the host-resolved country. Include exactly once in the application config.
 */
export const envProviders: Provider[] = [
  {
    provide: CountryToken,
    useFactory: resolveCountry,
  },
  {
    provide: EnvToken,
    useFactory: (country: CountryCode): AppEnv => pickEnv(country),
    deps: [CountryToken],
  },
  {
    provide: LanguageToken,
    useFactory: resolveLanguage,
  },
  {
    provide: LOCALE_ID,
    useFactory: (language: LanguageCode) =>
      resolveBcp47FromLanguageCode(language),
    deps: [LanguageToken],
  },
  {
    provide: SeoSegmentsToken,
    useFactory: (country: CountryCode): SeoSegments => seoSegments(country),
    deps: [CountryToken],
  },
  {
    provide: AppStoreToken,
    useFactory: (country: CountryCode): AppStore =>
      getCountryConfig(country).appStore,
    deps: [CountryToken],
  },
];
